<template>
  <div class="copywriting-view">
    <div class="cw-toolbar">
      <span class="cw-label">产品文案：</span>
      <button
        class="cw-generate-btn cw-generate-all"
        @click="workbench.generateAllCopywriting()"
        :disabled="anyLoading || !anyProductHasImage"
      >
        {{ anyLoading ? '生成中...' : '全部生成' }}
      </button>
      <button class="clear-task-btn" @click="onClearTask">清空任务</button>
    </div>

    <div class="cw-rows">
      <div v-for="(product, idx) in workbench.products" :key="product.id" class="cw-row">
        <div class="cw-row-header">
          <span class="cw-row-name">{{ product.name }}</span>
          <div class="cw-row-actions">
            <button
              class="cw-generate-btn cw-generate-one"
              :disabled="workbench.copywriting.loading[idx] || !product.mainImage"
              @click="workbench.generateCopywriting(idx)"
            >
              {{ workbench.copywriting.loading[idx] ? '生成中...' : '生成' }}
            </button>
            <button class="clear-product-btn" @click="workbench.clearProduct(idx)">清空</button>
            <button
              v-if="workbench.copywriting.results[idx]?.titleZh"
              class="cw-toggle-zh"
              :class="{ active: showZh[idx] }"
              @click="showZh[idx] = !showZh[idx]"
            >
              {{ showZh[idx] ? '隐藏中文' : '显示中文' }}
            </button>
          </div>
        </div>

        <div class="cw-supplement">
          <div v-if="workbench.isWomenClothing" class="cw-supplement-row cw-supplement-row-fabric">
            <label class="cw-supplement-label">面料材质</label>
            <input
              type="text"
              class="cw-fabric-input"
              v-model="workbench.copywriting.inputs[idx].fabric"
              placeholder="请输入面料材质"
            />
          </div>
          <div class="cw-supplement-row cw-supplement-row-selling">
            <label class="cw-supplement-label">重点卖点</label>
            <textarea
              class="cw-selling-input"
              v-model="workbench.copywriting.inputs[idx].sellingPoints"
              placeholder="选填：输入希望突出的卖点（如防水防油、加厚耐磨、便携折叠等），留空则由 AI 自行从图片判断"
              rows="2"
            ></textarea>
          </div>
          <div v-if="workbench.isWomenClothing" class="cw-supplement-row">
            <div class="cw-supplement-field">
              <label class="cw-supplement-label">尺码类型</label>
              <div class="cw-segmented">
                <button
                  class="cw-seg-btn"
                  :class="{ active: workbench.copywriting.inputs[idx].sizeType === 'multi' }"
                  @click="workbench.copywriting.inputs[idx].sizeType = 'multi'"
                >区分尺码</button>
                <button
                  class="cw-seg-btn"
                  :class="{ active: workbench.copywriting.inputs[idx].sizeType === 'one-size' }"
                  @click="workbench.copywriting.inputs[idx].sizeType = 'one-size'"
                >均码</button>
              </div>
            </div>
            <div class="cw-supplement-field">
              <label class="cw-supplement-label">尺码表</label>
              <div class="cw-segmented">
                <button
                  class="cw-seg-btn"
                  :class="{ active: workbench.copywriting.inputs[idx].hasSizeChart === 'yes' }"
                  @click="workbench.copywriting.inputs[idx].hasSizeChart = 'yes'"
                >有</button>
                <button
                  class="cw-seg-btn"
                  :class="{ active: workbench.copywriting.inputs[idx].hasSizeChart === 'no' }"
                  @click="workbench.copywriting.inputs[idx].hasSizeChart = 'no'"
                >无</button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="workbench.copywriting.errors[idx]" class="cw-error">{{ workbench.copywriting.errors[idx] }}</div>

        <div v-if="workbench.copywriting.loading[idx]" class="cw-loading">
          <div class="cw-spinner"></div>
          <span>AI 正在生成文案...</span>
        </div>

        <div v-else-if="workbench.copywriting.results[idx]" class="cw-result">
          <template v-if="workbench.copywriting.results[idx].title">
            <!-- 标题 -->
            <div class="cw-section">
              <h4>
                标题
                <span class="cw-char-count" :class="{ over: workbench.copywriting.results[idx].title.length > 75 }">
                  （{{ workbench.copywriting.results[idx].title.length }}/75字符）
                </span>
              </h4>
              <div class="cw-line">
                <p class="cw-content">{{ workbench.copywriting.results[idx].title }}</p>
                <button class="cw-copy-btn" @click="copyText(workbench.copywriting.results[idx].title)">复制</button>
              </div>
              <p v-if="showZh[idx] && workbench.copywriting.results[idx].titleZh" class="cw-content cw-zh">{{ workbench.copywriting.results[idx].titleZh }}</p>
            </div>

            <!-- 商品亮点 -->
            <div class="cw-section" v-if="workbench.copywriting.results[idx].highlights">
              <h4>
                商品亮点
                <span class="cw-char-count" :class="{ over: workbench.copywriting.results[idx].highlights.length > 125 }">
                  （{{ workbench.copywriting.results[idx].highlights.length }}/125字符）
                </span>
              </h4>
              <div class="cw-line">
                <p class="cw-content">{{ workbench.copywriting.results[idx].highlights }}</p>
                <button class="cw-copy-btn" @click="copyText(workbench.copywriting.results[idx].highlights)">复制</button>
              </div>
              <p v-if="showZh[idx] && workbench.copywriting.results[idx].highlightsZh" class="cw-content cw-zh">{{ workbench.copywriting.results[idx].highlightsZh }}</p>
            </div>

            <!-- 五点描述 -->
            <div class="cw-section" v-if="getFeatures(idx).length">
              <h4>五点描述</h4>
              <div v-for="(bullet, i) in getFeatures(idx)" :key="i" class="cw-bullet-group">
                <div class="cw-line">
                  <span class="cw-bullet-num">{{ i + 1 }}.</span>
                  <p class="cw-bullet-text">{{ bullet }}</p>
                  <button class="cw-copy-btn sm" @click="copyText(bullet)">复制</button>
                </div>
                <p v-if="showZh[idx] && workbench.copywriting.results[idx].featuresZh?.[i]" class="cw-bullet-text cw-zh">{{ workbench.copywriting.results[idx].featuresZh[i] }}</p>
              </div>
            </div>

            <!-- 产品描述 -->
            <div class="cw-section" v-if="getDesc(idx)">
              <h4>产品描述</h4>
              <div class="cw-line">
                <p class="cw-content">{{ getDesc(idx) }}</p>
                <button class="cw-copy-btn" @click="copyText(getDesc(idx))">复制</button>
              </div>
              <p v-if="showZh[idx] && workbench.copywriting.results[idx].introductionZh" class="cw-content cw-zh">{{ workbench.copywriting.results[idx].introductionZh }}</p>
            </div>

            <!-- 关键词 -->
            <div class="cw-section" v-if="workbench.copywriting.results[idx].keywords">
              <div class="cw-keywords-header">
                <h4 style="margin: 0;">搜索关键词</h4>
                <button
                  class="cw-radar-btn"
                  :disabled="workbench.copywriting.results[idx].loadingVolume"
                  @click="workbench.analyzeProductTraffic(idx)"
                >
                  <template v-if="workbench.copywriting.results[idx].loadingVolume">分析中...</template>
                  <template v-else-if="workbench.copywriting.results[idx].keywordVolumeData?.length">重新查热度</template>
                  <template v-else>查热度（约 {{ getKeywordsList(workbench.copywriting.results[idx].keywords).length }} 词 ≈ {{ getKeywordsList(workbench.copywriting.results[idx].keywords).length }} 算力）</template>
                </button>
              </div>
              <div v-if="workbench.copywriting.results[idx].volumeError" class="cw-volume-error">{{ workbench.copywriting.results[idx].volumeError }}</div>
              <div v-if="workbench.copywriting.results[idx].loadingVolume" class="cw-volume-progress">
                <div class="cw-spinner"></div>
                <span>正在查第 {{ workbench.copywriting.results[idx].currentVolumeIndex }}/{{ workbench.copywriting.results[idx].totalVolumeCount }} 个词...</span>
              </div>
              <div class="cw-keywords">
                <span
                  v-for="(kw, i) in getKeywordsList(workbench.copywriting.results[idx].keywords)"
                  :key="i"
                  class="cw-keyword-tag"
                  :class="tierClass(idx, kw)"
                  :title="tierDesc(idx, kw)"
                >
                  <span class="kw-text">{{ kw }}</span>
                  <span v-if="tierLabel(idx, kw)" class="kw-tier">{{ tierLabel(idx, kw) }}</span>
                </span>
              </div>
            </div>
          </template>

          <template v-else-if="workbench.copywriting.results[idx].rawText">
            <div class="cw-section">
              <h4>生成结果</h4>
              <p class="cw-content cw-pre">{{ workbench.copywriting.results[idx].rawText }}</p>
              <button class="cw-copy-btn" @click="copyText(workbench.copywriting.results[idx].rawText)">复制全部</button>
            </div>
          </template>
        </div>

        <div v-else class="cw-empty">
          <span>未生成，点击上方按钮生成文案</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue'
import { useWorkbenchStore } from '@/stores/useWorkbenchStore'

const workbench = useWorkbenchStore()

const onClearTask = () => {
  if (!confirm('确定要清空当前任务吗？\n\n所有产品（A/B/C）的上传图片和所有生成结果都会被删除，且无法恢复。')) return
  workbench.clearAllTask()
}

const showZh = reactive({ 0: false, 1: false, 2: false })

const anyLoading = computed(() => workbench.copywriting.loading.some(Boolean))
const anyProductHasImage = computed(() => workbench.products.some(p => p?.mainImage))

const getFeatures = (idx) => workbench.copywriting.results[idx]?.features || workbench.copywriting.results[idx]?.bullets || []
const getDesc = (idx) => workbench.copywriting.results[idx]?.introduction || workbench.copywriting.results[idx]?.description || ''

const getKeywordsList = (kw) => {
  if (!kw) return []
  if (typeof kw === 'string') return kw.split(',').map(s => s.trim()).filter(Boolean)
  if (Array.isArray(kw)) return kw
  return []
}

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    const ta = document.createElement('textarea')
    ta.value = text
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
  }
}

const TIER_INFO = {
  hot: {
    label: '热词',
    desc: '当前搜索热度高，适合优先关注、优先埋词、优先用于标题/五点/广告测试。（但不承诺未来一定继续高热）'
  },
  stable: {
    label: '覆盖',
    desc: '当前搜索热度中等或波动不大，适合放入五点、描述、后台词，作为基础流量覆盖。（这类词不是爆词，但也不是没价值，适合做长期覆盖）'
  },
  test: {
    label: '测试',
    desc: '当前搜索热度较低，但可能属于细分长尾词、风格词、新趋势词或精准人群词，不直接否定，可以少量测试。'
  }
}

const getKeywordTier = (idx, kw) => {
  const result = workbench.copywriting.results[idx]
  if (!result || !result.volumeReady) return null
  const list = (result.keywordVolumeData || []).slice()
  if (list.length === 0) return null
  list.sort((a, b) => b.hotness - a.hotness)
  const N = list.length
  const topN = Math.max(1, Math.ceil(N * 0.3))
  const bottomN = Math.max(1, Math.ceil(N * 0.3))
  const target = String(kw).trim().toLowerCase()
  const sortedIdx = list.findIndex(it => it.keyword.toLowerCase() === target)
  if (sortedIdx === -1) return null
  if (sortedIdx < topN) return 'hot'
  if (sortedIdx >= N - bottomN) return 'test'
  return 'stable'
}
const tierLabel = (idx, kw) => {
  const t = getKeywordTier(idx, kw)
  return t ? TIER_INFO[t].label : ''
}
const tierDesc = (idx, kw) => {
  const t = getKeywordTier(idx, kw)
  return t ? `${TIER_INFO[t].label}：${TIER_INFO[t].desc}` : ''
}
const tierClass = (idx, kw) => {
  const t = getKeywordTier(idx, kw)
  return t ? `tier-${t}` : ''
}
</script>

<style scoped>
.copywriting-view {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: -28px;
}
.cw-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.cw-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-body);
  white-space: nowrap;
}
/* 生成按钮：焦糖主按钮规范 */
.cw-generate-btn {
  padding: 7px 16px;
  background: var(--primary-gradient);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 13px;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
  white-space: nowrap;
  box-shadow: var(--shadow-primary);
}
.cw-generate-btn:disabled {
  background: var(--surface-muted);
  color: var(--text-sub);
  box-shadow: none;
  cursor: not-allowed;
}
.cw-generate-btn:not(:disabled):hover {
  box-shadow: var(--shadow-primary-hover);
  transform: translateY(-1px);
}
.cw-generate-btn:not(:disabled):active {
  transform: translateY(0);
}
.cw-generate-all {
  margin-left: auto;
}
.cw-generate-one {
  padding: 4px 14px;
  border-radius: 6px;
  font-size: 12px;
}
/* 清空按钮：白底描边，hover 转危险色（覆写全局 components.css 同名规则） */
.clear-task-btn,
.clear-product-btn {
  padding: 4px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--bg-white);
  color: var(--text-body);
  font-size: 12px;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
}
.clear-task-btn:hover,
.clear-product-btn:hover {
  border-color: var(--danger);
  color: var(--danger);
  background: var(--danger-soft);
}

/* 按行排列 */
.cw-rows {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.cw-row {
  background: var(--bg-white);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color);
  padding: 20px;
}
.cw-row-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color);
}
.cw-row-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-title);
}
.cw-row-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cw-supplement {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 560px;
  max-width: 100%;
  margin: 0 auto 16px;
  padding: 14px 16px;
  background: var(--surface-soft);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
}
.cw-supplement-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.cw-supplement-field {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cw-supplement-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-body);
  white-space: nowrap;
  min-width: 60px;
}
.cw-fabric-input {
  flex: 1;
  padding: 7px 12px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  font-size: 13px;
  background: var(--bg-white);
  color: var(--text-title);
  text-align: center;
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.cw-fabric-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.cw-fabric-input::placeholder {
  font-size: 12px;
  color: var(--text-sub);
}
.cw-supplement-row-selling {
  align-items: flex-start;
}
.cw-supplement-row-selling .cw-supplement-label {
  padding-top: 7px;
}
.cw-selling-input {
  flex: 1;
  padding: 7px 12px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  font-size: 13px;
  background: var(--bg-white);
  color: var(--text-title);
  resize: vertical;
  min-height: 32px;
  font-family: inherit;
  line-height: 1.5;
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.cw-selling-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.cw-selling-input::placeholder {
  font-size: 12px;
  color: var(--text-sub);
}
.cw-segmented {
  display: inline-flex;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--bg-white);
}
.cw-seg-btn {
  padding: 6px 16px;
  border: none;
  background: var(--bg-white);
  color: var(--text-body);
  font-size: 12px;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
  border-right: 1px solid var(--border-color);
}
.cw-seg-btn:last-child {
  border-right: none;
}
.cw-seg-btn:hover:not(.active) {
  background: var(--primary-softer);
}
.cw-seg-btn.active {
  background: var(--primary-soft);
  color: var(--primary-active);
  font-weight: 600;
}
.cw-toggle-zh {
  padding: 4px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--bg-white);
  color: var(--text-body);
  font-size: 12px;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
}
.cw-toggle-zh:hover {
  border-color: var(--primary-color);
  color: var(--primary-active);
  background: var(--primary-softer);
}
.cw-toggle-zh.active {
  background: var(--primary-soft);
  border-color: var(--primary-color);
  color: var(--primary-active);
}

/* 内容区 */
.cw-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-body);
  font-size: 13px;
  padding: 20px 0;
}
.cw-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--border-strong);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.cw-result {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.cw-section h4 {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-title);
  margin: 0 0 8px 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.cw-char-count {
  font-size: 11px;
  font-weight: 400;
  color: var(--text-sub);
}
.cw-char-count.over {
  color: var(--danger);
  font-weight: 500;
}
.cw-line {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.cw-content {
  font-size: 13px;
  color: var(--text-body);
  line-height: 1.6;
  margin: 0;
  flex: 1;
  white-space: pre-wrap;
}
.cw-pre {
  white-space: pre-wrap;
  word-break: break-all;
}
.cw-zh {
  color: var(--text-sub);
  font-size: 12px;
  margin-top: 4px;
  padding: 6px 10px;
  background: var(--surface-soft);
  border-radius: var(--radius-sm);
  border-left: 3px solid var(--primary-color);
}
.cw-bullet-group {
  margin-bottom: 8px;
}
.cw-bullet-num {
  color: var(--primary-active);
  font-weight: 600;
  flex-shrink: 0;
  font-size: 13px;
}
.cw-bullet-text {
  font-size: 13px;
  color: var(--text-body);
  line-height: 1.5;
  margin: 0;
  flex: 1;
}
.cw-copy-btn {
  padding: 2px 8px;
  font-size: 11px;
  border: 1px solid var(--border-strong);
  border-radius: 6px;
  background: var(--bg-white);
  color: var(--text-body);
  cursor: pointer;
  flex-shrink: 0;
  transition: all var(--dur-fast) var(--ease-out);
}
.cw-copy-btn:hover {
  border-color: var(--primary-color);
  color: var(--primary-active);
  background: var(--primary-softer);
}
.cw-copy-btn.sm {
  padding: 1px 6px;
  font-size: 10px;
}
.cw-keywords-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.cw-radar-btn {
  padding: 4px 12px;
  background: var(--primary-gradient);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 12px;
  cursor: pointer;
  transition: all var(--dur-fast) var(--ease-out);
  box-shadow: var(--shadow-primary);
}
.cw-radar-btn:disabled {
  background: var(--surface-muted);
  color: var(--text-sub);
  box-shadow: none;
  cursor: not-allowed;
}
.cw-radar-btn:not(:disabled):hover {
  box-shadow: var(--shadow-primary-hover);
  transform: translateY(-1px);
}
.cw-radar-btn:not(:disabled):active {
  transform: translateY(0);
}
.cw-volume-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  color: var(--text-sub);
  font-size: 12px;
}
.cw-volume-error {
  color: var(--danger);
  font-size: 12px;
  padding: 6px 10px;
  background: var(--danger-soft);
  border-radius: 6px;
  margin-bottom: 8px;
}
.cw-keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.cw-keyword-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  background: var(--primary-soft);
  color: var(--primary-active);
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 11px;
  cursor: default;
}
.cw-keyword-tag.tier-hot {
  background: linear-gradient(135deg, #fff1f0 0%, #fff7e6 100%);
  color: var(--danger);
  border-color: var(--danger);
  font-weight: 600;
}
.cw-keyword-tag.tier-stable {
  background: var(--primary-softer);
  color: var(--primary-active);
  border-color: var(--primary-color);
}
.cw-keyword-tag.tier-test {
  background: var(--surface-muted);
  color: var(--text-sub);
  border-color: var(--border-color);
}
.kw-tier {
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.3px;
}
.cw-keyword-tag.tier-hot .kw-tier {
  background: var(--danger);
  color: #fff;
}
.cw-keyword-tag.tier-stable .kw-tier {
  background: var(--primary-color);
  color: #fff;
}
.cw-keyword-tag.tier-test .kw-tier {
  background: var(--text-sub);
  color: #fff;
}
.cw-error {
  color: var(--danger);
  font-size: 12px;
  padding: 6px 10px;
  background: var(--danger-soft);
  border-radius: 6px;
}
.cw-empty {
  color: var(--text-sub);
  font-size: 13px;
  padding: 20px 0;
}
</style>
